import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { SubscriptionInterval, ServiceTheme, AssetValidationStatus } from '@/types/service';
import { formatPrice } from '@/types/service';
import { useAssetValidator } from '@/hooks/use-asset-validator';
import { useStylesheetLoader } from '@/hooks/use-stylesheet-loader';
import type { DeviceMode } from './preview-device-toggle';
import './payment-page-preview.css';

interface PaymentPagePreviewProps {
  /** Service description shown to users */
  description: string;
  /** Amount in cents */
  grossAmount: number;
  /** Subscription interval */
  subscriptionInterval: SubscriptionInterval;
  /** Theme configuration */
  theme?: ServiceTheme;
  /** Device preview mode */
  deviceMode?: DeviceMode;
  /** Additional CSS classes */
  className?: string;
  /** Callback when asset validation status changes */
  onAssetValidation?: (status: AssetValidationStatus) => void;
}

/**
 * Live preview of the payment page as it will appear to end users.
 * Phase 3: With stylesheet loading and CSS sandboxing.
 */
export function PaymentPagePreview({
  description,
  grossAmount,
  subscriptionInterval,
  theme,
  deviceMode = 'mobile',
  className,
  onAssetValidation,
}: PaymentPagePreviewProps) {
  const { t } = useTranslation('services');
  const buttonText = t(`buttonText.${String(theme?.buttonTextId ?? 1)}`);
  const priceText = grossAmount > 0
    ? formatPrice(grossAmount) + ' / ' + t(`intervalShort.${subscriptionInterval}`)
    : '0,00 \u20AC / ---';

  // Asset validation hooks
  const headerImage = useAssetValidator(theme?.headerImageUrl, 'image');
  const backgroundImage = useAssetValidator(theme?.backgroundImageUrl, 'image');
  const stylesheet = useStylesheetLoader(theme?.stylesheetUrl);

  // Report asset validation status to parent
  useEffect(() => {
    if (onAssetValidation) {
      onAssetValidation({
        headerImage: headerImage.status,
        backgroundImage: backgroundImage.status,
        stylesheet: stylesheet.status,
      });
    }
  }, [headerImage.status, backgroundImage.status, stylesheet.status, onAssetValidation]);

  return (
    <div
      className={cn(
        'preview-container',
        deviceMode === 'desktop' && 'preview-container--desktop',
        className
      )}
      role="img"
      aria-label="Payment Page Vorschau"
    >
      {/* Injected stylesheet — safe: CSS is sandboxed via prefix scope in useStylesheetLoader */}
      {stylesheet.prefixedCss && (
        <style dangerouslySetInnerHTML={{ __html: stylesheet.prefixedCss }} />
      )}

      {/* Background Image (if loaded) */}
      {backgroundImage.loadedUrl && (
        <img
          src={backgroundImage.loadedUrl}
          alt=""
          className="preview-background-image"
          aria-hidden="true"
        />
      )}

      {/* Header Area */}
      <div className="preview-header">
        {headerImage.loadedUrl ? (
          <img
            src={headerImage.loadedUrl}
            alt={t('preview.headerImage', { defaultValue: 'Service Header' })}
            className="preview-header__image"
          />
        ) : headerImage.status.status === 'loading' ? (
          <div className="preview-header__placeholder preview-header__placeholder--loading">
            <span className="text-xs text-muted-foreground">Lädt...</span>
          </div>
        ) : headerImage.status.status === 'error' ? (
          <div className="preview-header__placeholder preview-header__placeholder--error">
            <span className="text-xs text-destructive">
              {headerImage.status.message}
            </span>
          </div>
        ) : theme?.headerImageUrl ? (
          <div className="preview-header__placeholder preview-header__placeholder--with-url">
            <span className="text-xs text-muted-foreground">Header-Bild</span>
            <span className="text-xs text-muted-foreground truncate max-w-full">
              {theme.headerImageUrl.length > 30
                ? `${theme.headerImageUrl.slice(0, 30)}...`
                : theme.headerImageUrl}
            </span>
          </div>
        ) : (
          <div className="preview-header__placeholder">
            <span className="text-xs text-muted-foreground">Kein Header-Bild</span>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="preview-content">
        {/* Description */}
        <h2 className="preview-description">
          {description || 'Service-Beschreibung'}
        </h2>

        {/* Price */}
        <p className="preview-price">{priceText}</p>

        {/* MSISDN Input (Mock) */}
        <div className="preview-msisdn">
          <input
            type="text"
            className="preview-msisdn__input"
            placeholder="49"
            disabled
            aria-label="Telefonnummer (Vorschau)"
          />
        </div>

        {/* Submit Button */}
        <button
          type="button"
          className="preview-button"
          disabled
          aria-label="Kaufen Button (Vorschau)"
        >
          {buttonText}
        </button>
      </div>

      {/* Footer */}
      <div className="preview-footer">
        <span>Datenschutz</span>
        <span className="preview-footer__separator">|</span>
        <span>AGB</span>
        <span className="preview-footer__separator">|</span>
        <span>Impressum</span>
      </div>

      {/* Background Status Indicator (when URL set but not loaded) */}
      {theme?.backgroundImageUrl && !backgroundImage.loadedUrl && (
        <div className={cn(
          'preview-background-hint',
          backgroundImage.status.status === 'error' && 'preview-background-hint--error'
        )}>
          <span className="text-xs">
            {backgroundImage.status.status === 'loading' && 'Hintergrund wird geladen...'}
            {backgroundImage.status.status === 'error' && `Hintergrund: ${backgroundImage.status.message}`}
            {backgroundImage.status.status === 'empty' && `Hintergrund: ${theme.backgroundImageUrl.slice(0, 25)}...`}
          </span>
        </div>
      )}

      {/* Stylesheet Status Indicator */}
      {theme?.stylesheetUrl && stylesheet.status.status !== 'success' && (
        <div className={cn(
          'preview-stylesheet-hint',
          stylesheet.status.status === 'error' && 'preview-stylesheet-hint--error'
        )}>
          <span className="text-xs">
            {stylesheet.status.status === 'loading' && 'Stylesheet wird geladen...'}
            {stylesheet.status.status === 'error' && `Stylesheet: ${stylesheet.status.message}`}
          </span>
        </div>
      )}
    </div>
  );
}
